import { useMemo, useState } from "react";
import { Minus, Plus, Search, Star, Trash2 } from "lucide-react";
import { CATEGORIES, imgUrl, rupiah } from "@/lib/api";

export const PosMenu = ({ menu, onAdd }) => {
  const [cat, setCat] = useState("semua");
  const [q, setQ] = useState("");
  const shown = useMemo(() => menu.filter((m) => (cat === "semua" || m.category === cat) && m.name.toLowerCase().includes(q.toLowerCase())), [menu, cat, q]);
  const cats = CATEGORIES.filter((c) => c.id === "semua" || menu.some((m) => m.category === c.id));
  const favs = useMemo(() => {
    const ranked = menu.filter((m) => m.rank > 0).sort((a, b) => a.rank - b.rank);
    return [...ranked, ...menu.filter((m) => !(m.rank > 0))].slice(0, 6);
  }, [menu]);
  return (
    <div data-testid="pos-menu">
      {favs.length > 0 && (
        <div className="mb-4" data-testid="pos-favorites">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted2 mb-2 flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400" /> Favorit (terlaris)</div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {favs.map((m, i) => (
              <button key={m.id} onClick={() => onAdd(m)} className="rounded-xl bg-forest text-paper px-2 py-3 text-left active:scale-95 transition-transform hover:bg-forest-dark min-h-[64px]" data-testid={`pos-fav-${m.id}`}>
                <div className="text-[10px] opacity-75">#{i + 1}</div>
                <div className="text-sm font-semibold leading-tight line-clamp-2">{m.name}</div>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari menu" className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-sage-200 bg-paper text-sm" data-testid="pos-search-input" />
        </div>
        {cats.map((c) => (
          <button key={c.id} onClick={() => setCat(c.id)} className={`px-4 py-2.5 rounded-full text-sm font-semibold ${cat === c.id ? "bg-forest text-paper" : "bg-sage-100"}`} data-testid={`pos-cat-${c.id}`}>{c.label}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 mt-4">
        {shown.map((m) => (
          <button key={m.id} onClick={() => onAdd(m)} className="card-paper overflow-hidden text-left active:scale-95 transition-transform hover:border-forest" data-testid={`pos-item-${m.id}`}>
            <img src={imgUrl(m.image)} alt={m.name} className="w-full aspect-[4/3] object-cover" loading="lazy" />
            <div className="p-3"><div className="font-semibold text-sm leading-tight line-clamp-2">{m.name}</div><div className="text-forest font-bold text-sm mt-1">{rupiah(m.price)}</div></div>
          </button>
        ))}
        {shown.length === 0 && <p className="col-span-full text-center text-muted2 py-10">Menu tidak ditemukan.</p>}
      </div>
    </div>
  );
};

const btn = "w-9 h-9 rounded-full bg-sage-100 grid place-items-center active:scale-90 transition-transform";

export const PosCartLines = ({ lines, setLines }) => {
  const upd = (id, patch) => setLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)).filter((l) => l.qty > 0));
  if (!lines.length) return <p className="text-center text-muted2 text-sm py-8" data-testid="pos-cart-empty">Ketuk menu untuk menambah.</p>;
  return (
    <ul className="divide-y divide-sage-200" data-testid="pos-cart-lines">
      {lines.map((l) => (
        <li key={l.id} className="py-2.5" data-testid={`pos-line-${l.id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0"><div className="font-semibold text-sm truncate">{l.name}</div><div className="text-xs text-muted2">{rupiah(l.price)}</div></div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button className={btn} onClick={() => upd(l.id, { qty: l.qty - 1 })} aria-label="Kurangi" data-testid={`pos-line-minus-${l.id}`}>{l.qty === 1 ? <Trash2 className="w-4 h-4 text-red-600" /> : <Minus className="w-4 h-4" />}</button>
              <span className="w-6 text-center font-bold" data-testid={`pos-line-qty-${l.id}`}>{l.qty}</span>
              <button className={btn} onClick={() => upd(l.id, { qty: l.qty + 1 })} aria-label="Tambah" data-testid={`pos-line-plus-${l.id}`}><Plus className="w-4 h-4" /></button>
            </div>
          </div>
          <input value={l.note} onChange={(e) => upd(l.id, { note: e.target.value.slice(0, 100) })} placeholder="Catatan (less sugar, tanpa es...)" className="mt-1.5 w-full px-2.5 py-1.5 rounded-md border border-sage-200 bg-white/60 text-xs" data-testid={`pos-line-note-${l.id}`} />
        </li>
      ))}
    </ul>
  );
};
